/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pick, pickBy } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import moment from 'moment';
import { PharmacyConsent } from 'app/types/pharmacy-consents';
import { formFields } from './form-fields';
import { AddConsentWizardModal } from './add-consent-wizard-modal';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyConsent> => [
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr',
    render: item => {
      return item.user?.name ?? 'N/A';
    }
  },
  {
    title: translate('common.table.columns.phoneNumber'),
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    size: '1fr',
    render: item => item.phoneNumber
  },
  {
    title: translate('common.table.columns.externalId'),
    dataIndex: 'externalId',
    key: 'externalId',
    render: value => value.externalId
  },
  {
    title: translate('common.table.columns.status'),
    dataIndex: 'status',
    key: 'status',
    render: value => value.status
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: value => <DateTime date={value.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<PharmacyConsent> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.subscriptionsDetail.pharmacies.consents,
    enableExport: true,
    addButton: translate('common.actions.add'),
    canEdit: true,
    extraActions: ({ refresh }) => {
      return <AddConsentWizardModal refresh={refresh} />;
    },
    canRemove: true,
    resolveParams: (params: any, item: any) => {
      return {
        phoneNumber: params?.phoneNumber ?? item?.phoneNumber,
        pharmacyId: params?.pharmacyId ?? item?.pharmacyId
      };
    },
    key: ['pharmaciesConsents'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;

      const result: any = {
        filters: {
          ...pickBy(filters, identity)
        },
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.pharmaciesConsents,
    endpointById: apiEndpoints.pharmaciesConsentsByPhoneNumber
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<PharmacyConsent> => ({
  endpoint: apiEndpoints.pharmaciesConsentsByPhoneNumber,
  resolveParams: (params: any, item?: PharmacyConsent) => {
    return {
      pharmacyId: params?.pharmacyId,
      phoneNumber: params?.phoneNumber ?? item?.phoneNumber
    };
  }
});

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<PharmacyConsent> => ({
  formFields: formFields(translate, 'add'),
  normalizePayload(values) {
    return {
      ...values,
      metadata: {
        createdByAdmin: true,
        externalId: values.externalId
      }
    };
  }
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<PharmacyConsent> => ({
  formFields: formFields(translate, 'edit'),
  endpoint: apiEndpoints.pharmaciesConsentsByPhoneNumber,
  redirect: appRoutes.dashboard.services.pharmacies.consents,
  resolveParams: (params: any) => {
    return {
      pharmacyId: params?.pharmacyId,
      phoneNumber: params?.phoneNumber
    };
  },
  route: appRoutes.dashboard.services.pharmacies.consentsEdit,
  submitLabel: translate('common.actions.edit'),
  normalizePayload(values) {
    return {
      status: values.status,
      externalId: values.externalId
    };
  },
  normalizeInitialValues: (keys: string[], payload: PharmacyConsent) => {
    const data: any = pick(payload, keys);

    data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
    data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

    return data;
  }
});

/**
 * Config.
 */

export function createSubscriptionsDetailPharmaciesConsentsTemplate(
  translate: TFunction
): CrudTemplate<PharmacyConsent> {
  return normalizeTemplate(translate, {
    list: list(translate),
    edit: edit(translate),
    remove: remove(),
    add: add(translate)
  });
}
